/**
 * Learner API
 *
 * @author minkyoung.park@emcast.com
 */
import httpService from "@/api/httpService";

const learnApi = {
  // 나의 학습목록(get learning list)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-degree-learner-page
  fetchList(params) {
    return httpService.get(`/learn/learner/online`, params);
  },

  fetchCategoryList() {
    return httpService.get(`/codes/code/DEGREE_CATEGORY/combo`)
  },

  // 나의 학습 달성률(get learning progress rate)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-degree-learner-progressrate-detail
  learnProgressRate(params) {
    return httpService.get(
      `/learn/learner/online/${params.id}/progressRate/detail`
    );
  },

  // 나의 학습 상세 기본정보(get learning basic information)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-degree-learner-detail
  learnViewBasic(params) {
    return httpService.get(`/learn/learner/online/${params.learnId}`);
  },

  // 나의 학습 상세 레슨 목록(get learning courses list)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-degree-learner-resource-courses
  learnViewCourses(params) {
    return httpService.get(`/learn/learner/online/${params.learnId}/courses`);
  },

  // 나의 학습 상세 시험 목록(get learning exams list)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-degree-learner-resource-exams
  learnViewExams(params) {
    return httpService.get(`/learn/learner/online/${params.learnId}/exams`);
  },

  // 나의 학습 상세 시험 목록(get learning survey list)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner_my_course.html#resources-degree-learner-fetch-survey
  learnViewSurveys(params) {
    return httpService.get(`/learn/learner/online/${params.learnId}/surveys`);
  },

  // 나의 학습 상세 미션 목록(get learning missions list)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-degree-learner-mission-list
  learnViewMission(params) {
    return httpService.get(`/learn/learner/online/${params.learnId}/mission`);
  },

  // 나의 학습 상세 미션 새소식 알림(get learning missions notice information)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-degree-learner-mission-notify-exists
  learnViewMissionNotice(params) {
    return httpService.get(
      `/learn/learner/online/${params.learnId}/mission/exists/notify`
    );
  },

  // 나의 학습 상세 미션 상세(get learning mission detail)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-degree-learner-mission-detail
  learnViewMissionView(params) {
    return httpService.get(
      `/learn/learner/online/${params.learnId}/mission/${params.assignmentId}`
    );
  },

  // 나의 학습 상세 미션 제출(submit learning mission)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-degree-learner-mission-register
  learnViewMissionSubmit(params) {
    return httpService.post(
      `/learn/learner/online/${params.learnId}/mission/${params.assignmentId}`,
      params
    );
  },

  // 나의 학습 상세 미션 수정(update learning mission)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-degree-learner-mission-modify
  learnViewMissionUpdate(params) {
    return httpService.put(
      `/learn/learner/online/${params.learnId}/mission/${params.assignmentId}/${params.answerId}`,
      params
    );
  },

  // 나의 학습 상세 미션 피드백 목록(get learning mission feedback list)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-degree-learner-mission-feedback-list
  learnViewMissionFeedback(params) {
    return httpService.get(
      `/learn/learner/online/${params.learnId}/mission/${params.assignmentId}/feedback`,
      params
    );
  },

  // 나의 학습 상세 미션 피드백 등록(regist learning mission feedback)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-degree-learner-mission-feedback-register
  learnViewMissionFeedbackSubmit(params) {
    return httpService.post(
      `/learn/learner/online/${params.learnId}/mission/${params.assignmentId}/feedback`,
      params
    );
  },

  // 나의 학습 상세 미션 피드백 삭제(delete learning mission feedback)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-degree-learner-mission-feedback-delete
  learnViewMissionFeedbackDelete(params) {
    return httpService.delete(
      `/learn/learner/online/${params.learnId}/mission/${params.assignmentId}/feedback/${params.feedbackId}`
    );
  },

  // 나의 학습 시작하기(set learning start)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-learner-degree-start
  learnStart(params) {
    return httpService.post(`/learn/learner/online/${params.learnId}/start`);
  },

  // 나의 학습 상세 - 다음 학습(get next lesson)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-degree-learner-resource-lesson-next
  learnLessonNext(params) {
    return httpService.get(
      `/learn/learner/online/${params.learnId}/lesson/next`
    );
  },

  // 학습목표(get lesson objective)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-online-learner-degree-lesson-intro
  learnLessonObjective(params) {
    return httpService.get(
      `/learn/learner/online/${params.learnId}/lesson/${params.progressionId}/intro`
    );
  },

  // 학습 차시 컨텐츠(get lesson contents)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-learner-degree-lesson-contents
  learnLessonContents(params) {
    return httpService.get(
      `/learn/learner/online/${params.learnId}/lesson/${params.progressionId}/contents`
    );
  },

  // 학습 - 차시 진도율 업데이트(update lesson progression)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-learner-increment-progression
  learnLessonProgression(params) {
    return httpService.put(
      `/learn/learner/online/${params.learnId}/lesson/${params.progressionId}/progression`,
      params
    );
  },

  // 좋아요(set lesson like)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-learner-lesson-prog-likes-register
  learnLessonLikesPost(params) {
    return httpService.post(
      `/learn/learner/online/${params.learnId}/lesson/${params.progressionId}/likes`
    );
  },

  // 좋아요 취소(cancel lesson like)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-learner-lesson-prog-likes-remove
  learnLessonLikesDelete(params) {
    return httpService.delete(
      `/learn/learner/online/${params.learnId}/lesson/${params.progressionId}/likes`
    );
  },

  // 북마크(set lesson bookmark)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-learner-lesson-prog-clipping-register
  learnLessonClippingPost(params) {
    return httpService.post(
      `/learn/learner/online/${params.learnId}/lesson/${params.progressionId}/clipping`
    );
  },

  // 북마크 취소(cancel lesson bookmark)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-learner-lesson-prog-clipping-remove
  learnLessonClippingDelete(params) {
    return httpService.delete(
      `/learn/learner/online/${params.learnId}/lesson/${params.progressionId}/clipping`
    );
  },

  // 학습 - 차시 추가 자료 or 퀴즈(get lesson Additional Materials or Quiz)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-learner-lesson-timelinecontents
  learnLessonTimelinecontents(params) {
    return httpService.get(
      `/learn/learner/online/${params.learnId}/lesson/${params.progressionId}/timelinecontents`
    );
  },

  // 나의 학습 상세 -주문 정보(get lesson order information)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner.html#resources-degree-learner-resource-order
  learnOrderInfo(params) {
    return httpService.get(`/learn/learner/online/${params.learnId}/order`);
  },

  // 나의 학습 채팅방 목록(get lesson question chatting room list)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learn_chat.html#resources-online-learner-degree-chatroom-page
  learnChatroomList(params) {
    return httpService.get(
      `/learn/learner/online/${params.learnId}/chatroom?sort=id,ASC`
    );
  },

  // 북마크(set Mission bookmark)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner_learn.html#resources-learner-mission-clipping
  learnMissionClippingPost(params) {
    return httpService.post(
      `/learn/learner/online/${params.learnId}/mission/${params.assignmentId}/clipping`
    );
  },

  // 북마크(cancel Mission bookmark)
  // https://clpapi.tinkeredu.co.kr/static/docs/online_learner_learn.html#resources-learner-mission-unClipping
  learnMissionClippingDelete(params) {
    return httpService.delete(
      `/learn/learner/online/${params.learnId}/mission/${params.assignmentId}/unClipping`
    );
  },
  learningStatus(learnId) {
    return httpService.get(`/learn/learner/online/${learnId}/learningStatus`);
  },

  //     - API Address-

  // GET http://localhost:8090/learn/subject/ /degree
  subjectList(params) {
    return httpService.get(`/learn/subject/${params.subjectId}/degree`);
  },

  getMemberInfo(params) {
    return httpService.get("/membership/member", params);
  },

  // (count Ongoing, Completed)
  // http://13.230.87.17:8080/static/docs/online_learner_my_course.html#resources-degree-learner-count
  learnCountLesson(params) {
    return httpService.get(`/learn/learner/online/count`, params);
  },

  createComputerScienceUser(idx) {
    return new Promise((resolve, reject) => {
      httpService
        .post(`/learn/learner/online/cs/register/${idx}`)
        .then(response => {
          resolve(response.data.data);
        })
        .catch(err => {
          console.log("Error createComputerScienceUser : params => ", idx);
          console.log("Error createComputerScienceUser => ", err);
        });
    });
  },

  registerComputerScienceContents(params) {
    return new Promise((resolve, reject) => {
      httpService
        .post(`/learn/learner/online/cs`, params)
        .then(response => {
          resolve(response.data.data);
        })
        .catch(err => {
          console.log(
            "Error registerComputerScienceContents : params => ",
            params
          );
          console.log("Error => ", err);
        });
    });
  },

  getComputerScienceWebSocketUrl(params) {
    return new Promise((resolve, reject) => {
      httpService
        .get(`/learn/learner/online/cs/socket`, params)
        .then(response => {
          resolve(response.data.data);
        })
        .catch(err => {
          console.log(
            "Error registerComputerScienceContents : params => ",
            params
          );
          console.log("Error => ", error);
        });
    });
  },

  getComputerScienceSession(params) {
    return new Promise((resolve, reject) => {
      httpService
          .get(`/learn/learner/online/cs/session`, params)
          .then(response => {
            resolve(response.data.data);
          })
          .catch(err => {
            console.log(
                "Error Read ComputerScienceSession : params => ",params
            );
            console.log("Error => ", error);
          });
    });
  },

  // 과제 히스토리 조회
  // https://dev.clp.kr/api/static/docs/online_learner_my_course.html#resources-front-mission-history
  learnMissionHistory(id) {
    return httpService.get(`/mission/answer/${id}/history`);
  },

  computerScienceServerStop() {
    return httpService.put(`/learn/learner/online/cs/server/stop`);
  },

  // 과정 오픈 (Next Learning Open)
  // https://dev.clp.kr/spc/api/static/docs/front/online_learner_my_course.html#resources-degree-learner-resource-course-open
  openNextLearning(learnerId) {
    return httpService.put(`/learn/learner/online/${learnerId}/course/open`);
  },
};

// export const
export default learnApi;
